import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { UserBadge } from '../DocumentPreview';
import { Clock, ExternalLink, Mail, MessageSquarePlus, Phone, Users, UserCheck, ArrowRightLeft } from 'lucide-react';
import { DaysBadge } from './shared';

export const OverdueRow = ({ row, isAdmin, onSnooze, onReassign, onOpenNote, showLastContact, showDateField, columnLabel }) => {
    const navigate = useNavigate();
    const goToDossier = () => {
        if (row.id && row.overdue_type !== 'client_silencieux' && row.overdue_type !== 'relance_en_retard') {
            navigate(`/dossiers/${row.id}`);
        } else if (row.overdue_type === 'relance_en_retard' && row.dossier_id_str) {
            navigate(`/dossiers/${row.dossier_id_str}`);
        } else if (row.overdue_type === 'client_silencieux' && row.prospect_id) {
            navigate(`/prospects/${row.prospect_id}`);
        }
    };

    return (
        <div
            className="grid grid-cols-12 gap-3 items-center p-3 hover:bg-[#FAFAFA] transition-colors"
            data-testid={`overdue-row-${row.id || row.prospect_id}`}
        >
            <div className="col-span-4 min-w-0">
                <button
                    onClick={goToDossier}
                    className="font-medium text-[#0A0A0A] hover:text-[#E85A24] truncate block text-left w-full"
                >
                    {row.titre || row.dossier_titre || row.prospect_nom || 'Sans titre'}
                </button>
                <p className="text-xs text-[#52525B] truncate flex items-center gap-2">
                    {row.prospect_nom && <span className="flex items-center gap-1"><Users className="w-3 h-3" />{row.prospect_nom}</span>}
                    {row.type_relance && <span className="px-1.5 py-0.5 bg-[#F4F4F5] rounded text-[10px] uppercase">{row.type_relance}</span>}
                </p>
            </div>

            <div className="col-span-2">
                {row.commercial_nom && (
                    <UserBadge name={row.commercial_nom} id={row.commercial_id || ''} />
                )}
            </div>

            <div className="col-span-2 text-xs text-[#52525B]">
                {showLastContact ? (
                    <div>
                        <div className="text-[10px] uppercase tracking-wide">Dernier contact</div>
                        <div>{row.last_contact ? new Date(row.last_contact).toLocaleDateString('fr-FR') : '—'}</div>
                    </div>
                ) : showDateField ? (
                    <div>
                        <div className="text-[10px] uppercase tracking-wide">{columnLabel || 'Date'}</div>
                        <div>{row[showDateField] ? new Date(row[showDateField]).toLocaleDateString('fr-FR') : '—'}</div>
                    </div>
                ) : null}
            </div>

            <div className="col-span-1">
                <DaysBadge days={row.days_late} />
            </div>

            <div className="col-span-3 flex items-center justify-end gap-1">
                {row.prospect_telephone && (
                    <Button
                        as="a"
                        variant="ghost"
                        size="icon"
                        asChild
                        className="h-8 w-8 text-[#16A34A] hover:bg-green-50"
                        title="Appeler"
                    >
                        <a href={`tel:${row.prospect_telephone}`} data-testid="overdue-call-btn">
                            <Phone className="w-4 h-4" />
                        </a>
                    </Button>
                )}
                {row.prospect_email && (
                    <Button
                        variant="ghost"
                        size="icon"
                        asChild
                        className="h-8 w-8 text-[#0284C7] hover:bg-blue-50"
                        title="Envoyer email"
                    >
                        <a href={`mailto:${row.prospect_email}`} data-testid="overdue-email-btn">
                            <Mail className="w-4 h-4" />
                        </a>
                    </Button>
                )}
                {onOpenNote && (
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={onOpenNote}
                        className="h-8 w-8"
                        title="Ajouter une note"
                        data-testid="overdue-note-btn"
                    >
                        <MessageSquarePlus className="w-4 h-4" />
                    </Button>
                )}
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={onSnooze}
                    className="h-8 w-8 text-[#D97706] hover:bg-amber-50"
                    title="Reporter / Marquer traité"
                    data-testid="overdue-snooze-btn"
                >
                    <Clock className="w-4 h-4" />
                </Button>
                {isAdmin && onReassign && (
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={onReassign}
                        className="h-8 w-8 text-[#7C3AED] hover:bg-purple-50"
                        title="Réassigner"
                        data-testid="overdue-reassign-btn"
                    >
                        <ArrowRightLeft className="w-4 h-4" />
                    </Button>
                )}
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={goToDossier}
                    className="h-8 w-8"
                    title="Ouvrir"
                    data-testid="overdue-open-btn"
                >
                    <ExternalLink className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );
};
